'use client';
import { useCallback, useState } from 'react';

import { safeCall } from '@/app/utils/safeCall';

interface DisclosureCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A hook for managing the open, close state and toggle handlers of a component.
 * @param defaultOpen - The initial open state of the component.
 * @param callbacks - (Optional) callbacks for the open/close events.
 * @returns An array containing the open state and the open, close and toggle functions.
 *
 * Read more: [Storybook docs: `useDisclosure`](https://tis-web-story-book.netlify.app/?path=/docs/hooks-usedisclosure--documentation)
 */
export const useDisclosure = (
  defaultOpen = false,
  callbacks?: DisclosureCallbacks
) => {
  const { onOpen, onClose } = callbacks || {};
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const handleOpen = useCallback(() => {
    setIsOpen((prevState) => {
      if (!prevState) {
        safeCall(onOpen);
        return true;
      }
      return prevState;
    });
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen((prevState) => {
      if (prevState) {
        safeCall(onClose);
        return false;
      }
      return prevState;
    });
  }, [onClose]);

  const handleToggle = useCallback(() => {
    isOpen ? handleClose() : handleOpen();
  }, [isOpen, handleClose, handleOpen]);

  return [
    isOpen,
    { open: handleOpen, close: handleClose, toggle: handleToggle },
  ] as const;
};
